/**
 * 高德地图插件 - JS SDK 加载器
 *
 * 特性：
 * - 单例加载：多次调用共享同一 Promise，避免重复插入 script 标签
 * - 幂等：AMap 已存在时直接返回，无副作用
 * - Key 为空时抛出明确错误，便于排查配置问题
 */
import { getAmapKey, getAmapScriptUrl } from './constants'

let amapLoadPromise: Promise<any> | null = null

const AMAP_SCRIPT_ID = 'hll-amap-loader-script'

const injectAmapScript = (scriptUrl: string) => {
  return new Promise<any>((resolve, reject) => {
    const runtimeWindow = window as any
    if (runtimeWindow.AMap) {
      resolve(runtimeWindow.AMap)
      return
    }

    let script = document.getElementById(AMAP_SCRIPT_ID) as HTMLScriptElement | null
    if (!script) {
      script = document.createElement('script')
      script.id = AMAP_SCRIPT_ID
      script.async = true
      script.src = scriptUrl
      document.head.appendChild(script)
    }

    const onLoad = () => {
      if (runtimeWindow.AMap) {
        resolve(runtimeWindow.AMap)
        return
      }
      reject(new Error('高德地图 SDK 加载失败：AMap 未定义'))
    }
    const onError = () => {
      reject(new Error('高德地图 SDK 加载失败：脚本加载或网络错误'))
    }

    // 用 addEventListener 替代 onload 赋值，避免覆盖已有回调；{ once: true } 行为与原先一致
    script.addEventListener('load', onLoad, { once: true })
    script.addEventListener('error', onError, { once: true })

    // DOM 里已有 script 且已加载完时，load 不会再次触发（如 SPA 二次进入该页）
    queueMicrotask(() => {
      if (runtimeWindow.AMap) {
        onLoad()
      }
    })
  })
}

/**
 * 加载高德 JS API SDK，返回 AMap 命名空间
 * @example
 *   const AMap = await loadAmapSdk()
 *   const map = new AMap.Map('container')
 */
export const loadAmapSdk = async () => {
  const runtimeWindow = window as any
  if (runtimeWindow.AMap) {
    return runtimeWindow.AMap
  }

  if (!amapLoadPromise) {
    const amapKey = getAmapKey()
    if (!amapKey) {
      throw new Error('高德地图 Key 未配置')
    }
    amapLoadPromise = injectAmapScript(getAmapScriptUrl(amapKey))
  }

  return amapLoadPromise
}
